import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RealtimeAgent, RealtimeSession } from '@openai/agents/realtime';
import api from '@/utils/ky';
import {
  CallState,
  LoadingState,
  UsePSAgentCallProps,
  PSAgentCallReturn,
  Transcript,
  SurveyProgress,
  PS_CONFIG,
  formatTime,
} from '@/types/ps-common';
import {
  generateOpenAIRealtimePrompt,
  OPENAI_REALTIME_CONFIG,
  EPHEMERAL_TOKEN_ENDPOINT_V2,
} from '@/lib/ps-realtime-config';
import {
  generateOpenAIRealtimePromptV3,
  OPENAI_REALTIME_CONFIG_V3,
} from '@/lib/ps-realtime-config-v3';
import { PS_SURVEY_FLOW } from '@/lib/ps-survey-questions';
import { PS_SURVEY_FLOW_V3 } from '@/lib/ps-survey-questions-v3';

// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;

export const useOpenAIRealtimeCall = ({
  userId,
  version = 2,
  lang = 'en',
  voice, // Not used in OpenAI Realtime implementation, but kept for interface compatibility
  onCallEnded,
}: UsePSAgentCallProps): PSAgentCallReturn => {
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<any>(null);

  // Transcript and conversation state
  const [transcript, setTranscript] = useState<Transcript[]>([]);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);

  // Survey state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [surveyResponses, setSurveyResponses] = useState<
    Record<string, string>
  >({});
  const [isWaitingForResponse, setIsWaitingForResponse] =
    useState<boolean>(false);

  // OpenAI Agents refs
  const sessionRef = useRef<any>(null);
  const agentRef = useRef<any>(null);

  // Pre-fetched ephemeral tokens per model for performance
  const ephemeralTokensRef = useRef<Record<string, string>>({});
  const tokenFetchPromisesRef = useRef<Record<string, Promise<string>>>({});

  // Select appropriate survey flow based on version
  const surveyFlow = version === 3 ? PS_SURVEY_FLOW_V3 : PS_SURVEY_FLOW;

  // Get current question
  const currentQuestion = surveyFlow[currentQuestionIndex]?.question || '';
  const surveyProgress: SurveyProgress = {
    current: currentQuestionIndex + 1,
    total: surveyFlow.length,
  };

  // Agent info - use appropriate name and avatar based on version
  // V3 with OpenAI Realtime uses female persona (Rachel)
  const agentInfo = {
    profile: {
      name: version === 3 ? PS_CONFIG.nameV3Realtime : PS_CONFIG.nameChinese,
      role: version === 3 ? 'Post-Transaction Check Representative' : 'Post-Sales Survey Representative',
      avatar: version === 3 ? PS_CONFIG.avatarV3Realtime : PS_CONFIG.avatar,
    },
  };

  // Setup call timer
  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  // Fetch ephemeral token with timeout and retry logic
  const fetchEphemeralToken = useCallback(
    async (model: string, voice: string): Promise<string> => {
      try {
        if (DEBUG) console.log('🔑 Fetching ephemeral token for OpenAI Realtime');
        // Add timeout of 30 seconds and retry logic
        const resp = await api.post(EPHEMERAL_TOKEN_ENDPOINT_V2, {
          timeout: 30000, // 30 seconds timeout
          retry: {
            limit: 2,
            methods: ['POST'],
            statusCodes: [408, 413, 429, 500, 502, 503, 504],
          },
          json: {
            model,
            voice,
          },
        });
        const { client_secret, success, error } = await resp.json<{
          success: boolean;
          client_secret?: string;
          error?: string;
        }>();

        if (!success || !client_secret) {
          throw new Error(
            `Token fetch failed: ${error || 'No client_secret returned'}`,
          );
        }

        ephemeralTokensRef.current[model] = client_secret;
        if (DEBUG) console.log('✅ Ephemeral token fetched successfully');
        return client_secret;
      } catch (error) {
        console.error('❌ Failed to fetch ephemeral token:', error);
        throw error;
      }
    },
    [],
  );

  // Get or fetch ephemeral token with improved error handling
  const getEphemeralToken = useCallback(
    async (model: string, voice: string): Promise<string> => {
      // Check if we have a valid cached token
      if (ephemeralTokensRef.current[model]) {
        if (DEBUG) console.log('🔑 Using cached ephemeral token');
        return ephemeralTokensRef.current[model];
      }

      // If there's already a fetch in progress, wait for it
      if (tokenFetchPromisesRef.current[model]) {
        if (DEBUG) console.log('🔑 Waiting for ongoing token fetch...');
        try {
          return await tokenFetchPromisesRef.current[model];
        } catch (error) {
          // If the ongoing fetch failed, clear it and try again
          console.warn('🔄 Ongoing fetch failed, will retry');
          delete tokenFetchPromisesRef.current[model];
          throw error;
        }
      }

      // Start a new fetch
      if (DEBUG) console.log('🔑 Starting new token fetch...');
      tokenFetchPromisesRef.current[model] = fetchEphemeralToken(model, voice);

      try {
        const token = await tokenFetchPromisesRef.current[model];
        return token;
      } catch (error) {
        if (DEBUG) console.log('❌ Token fetch failed:', error);
        // Clear the promise on error so next attempt can retry
        delete tokenFetchPromisesRef.current[model];
        throw new Error(
          'Unable to authenticate with OpenAI. Please check your connection and try again.',
        );
      }
    },
    [fetchEphemeralToken],
  );

  // Handle user response for survey flow tracking
  const handleUserResponse = useCallback(
    (response: string) => {
      const currentQuestionId = PS_SURVEY_FLOW[currentQuestionIndex]?.id;
      if (currentQuestionId) {
        setSurveyResponses((prev) => ({
          ...prev,
          [currentQuestionId]: response,
        }));

        // Move to next question automatically after response
        setTimeout(() => {
          setCurrentQuestionIndex((prev) => {
            const nextIndex = prev + 1;
            if (nextIndex >= PS_SURVEY_FLOW.length) {
              // Survey completed - will be handled by AI naturally
              return prev;
            }
            return nextIndex;
          });
        }, 2000); // Give time for AI response
      }
    },
    [currentQuestionIndex],
  );

  // Main connection handler using RealtimeSession
  const handleAnswerCall = useCallback(async () => {
    try {
      if (
        loadingState === LoadingState.CONNECTING ||
        loadingState === LoadingState.ACTIVE
      ) {
        return;
      }

      if (DEBUG) console.log('📞 Starting OpenAI Realtime call with @openai/agents');
      setLoadingState(LoadingState.CONNECTING);
      setError(null);

      // Generate session ID
      const newSessionId = `ps_realtime_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      setSessionId(newSessionId);
      setConversationId(newSessionId);

      // Get ephemeral token (pre-fetched or fetch now) for desired model
      const config = version === 3 ? OPENAI_REALTIME_CONFIG_V3 : OPENAI_REALTIME_CONFIG;
      const desiredModel = config.model;
      const desiredVoice = config.voice;
      const ephemeralKey = await getEphemeralToken(desiredModel, desiredVoice);
      if (DEBUG) console.log('🔑 Token type check:', {
        isString: typeof ephemeralKey === 'string',
        length: ephemeralKey?.length,
        prefix: ephemeralKey?.substring(0, 10),
      });
      if (typeof ephemeralKey !== 'string' || !ephemeralKey.startsWith('ek_')) {
        console.warn(
          '⚠️ Expected an OpenAI ephemeral client key (ek_*) for WebRTC, got:',
          ephemeralKey?.substring(0, 20),
        );
      }

      // Create RealtimeAgent with PS survey instructions
      const instructions = version === 3
        ? generateOpenAIRealtimePromptV3(lang)
        : generateOpenAIRealtimePrompt(lang);
      if (DEBUG) console.log('🤖 Creating RealtimeAgent with PS survey instructions (Version ' + version + ')');

      const agent = new RealtimeAgent({
        name: version === 3 ? PS_CONFIG.nameV3Realtime : PS_CONFIG.nameChinese,
        instructions: instructions,
      });
      agentRef.current = agent;

      // Create RealtimeSession with explicit WebRTC transport
      if (DEBUG) console.log('📡 Creating RealtimeSession with WebRTC transport');

      // Map language code to transcription language
      const getTranscriptionLanguage = (langCode: string) => {
        const langMap: Record<string, string> = {
          'zh': 'zh',
          'cmn': 'zh', // Mandarin -> Chinese
          'yue': 'zh', // Cantonese -> Chinese
          'ms': 'ms',
          'en': 'en',
        };
        return langMap[langCode] || langCode;
      };

      const session = new RealtimeSession(agent, {
        model: desiredModel,
        config: {
          // inputAudioFormat: 'pcm16',
          // outputAudioFormat: 'pcm16',
          inputAudioTranscription: {
            model: 'gpt-4o-transcribe', // Use full model for better accuracy
            language: getTranscriptionLanguage(lang), // Explicit language for accurate transcription
          },
          // Enable semantic VAD for Version 3 to handle filler words better
          // semantic_vad detects when user is done speaking based on meaning, not just silence
          // Language-specific eagerness:
          // - Mandarin (zh): 'low' - allows longer pauses for filler words like "嗯", "那個"
          // - English/Others: 'medium' - balanced response time without excessive delay
          ...(version === 3
            ? {
                turnDetection: {
                  type: 'semantic_vad' as const,
                  eagerness: ['zh', 'cmn', 'yue'].includes(lang) ? 'low' : 'medium',
                  createResponse: true,
                  interruptResponse: false, // Don't interrupt AI while speaking
                },
              }
            : {}),
          audio: {
            output: {
              voice: config.voice,
              speed: config.speed,
            },
          },
        },
      });
      sessionRef.current = session;

      startTimeRef.current = Date.now();

      // Set up event listeners for the session
      session.on('transport_event', (event: any) => {
        if (DEBUG) console.log('📡 Transport event:', event.type);

        if (event.type === 'session.created') {
          if (DEBUG) console.log('✅ OpenAI Realtime session created');
          setCallState(CallState.ACTIVE);
          setLoadingState(LoadingState.ACTIVE);
        }
      });

      // Handle transcript updates - this fixes duplication issue
      session.on('history_updated', (history: any[]) => {
        if (DEBUG) console.log('📝 History updated:', history.length, 'items');

        // Convert history to transcript format, avoiding duplicates
        const transcriptItems: Transcript[] = history
          .filter(
            (item) =>
              item.role && (item.role === 'user' || item.role === 'assistant'),
          )
          .map((item) => {
            // Extract content from different possible structures
            let content = '';

            if (typeof item.content === 'string') {
              content = item.content;
            } else if (Array.isArray(item.content)) {
              // Handle content array (e.g., [{ type: 'input_text', text: '...' }])
              content = item.content
                .filter((part: any) => part.text || part.transcript)
                .map((part: any) => part.text || part.transcript)
                .join(' ');
            } else if (item.content?.text) {
              content = item.content.text;
            } else if (item.content?.transcript) {
              content = item.content.transcript;
            } else if (item.transcript) {
              content = item.transcript;
            }

            return {
              source: item.role === 'user' ? 'user' : 'ai',
              message: content.trim(),
              timestamp: new Date(item.created_at || Date.now()),
            };
          })
          .filter((item) => item.message.length > 0);

        if (DEBUG) console.log('📋 Processed transcript items:', transcriptItems);
        setTranscript(transcriptItems);

        // Track user responses for survey flow
        const lastItem = history[history.length - 1];
        if (lastItem && lastItem.role === 'user') {
          const userContent =
            lastItem.content?.text || lastItem.content || lastItem.transcript;
          if (userContent) {
            handleUserResponse(userContent);
          }
        }
      });

      // Handle speaking status updates and transcript deltas
      session.on('transport_event', (event: any) => {
        if (event.type === 'response.audio.delta') {
          setIsSpeaking(true);
        } else if (event.type === 'response.done') {
          setIsSpeaking(false);
        } else if (DEBUG && event.type === 'response.output_audio_transcript.delta') {
          console.log('🎙️ AI speech transcript delta:', event.delta);
        } else if (DEBUG && event.type === 'conversation.item.input_audio_transcription.delta') {
          console.log('🎤 User speech transcript delta:', event.delta);
        } else if (DEBUG && event.type === 'conversation.item.input_audio_transcription.completed') {
          console.log('✅ User speech transcription completed:', event.transcript);
        } else if (DEBUG && event.type === 'response.output_audio_transcript.done') {
          console.log('✅ AI speech transcription done:', event.transcript);
        }
      });

      // Handle errors
      session.on('transport_event', (event: any) => {
        if (event.type === 'error') {
          console.error('❌ OpenAI Realtime error:', event.error);
          setError(event.error.message || 'OpenAI Realtime error occurred');
        }
      });

      // Connect to OpenAI Realtime API
      if (DEBUG) console.log('🔗 Connecting to OpenAI Realtime API...');
      await session.connect({
        apiKey: ephemeralKey,
      });

      if (DEBUG) console.log('✅ OpenAI Realtime call established successfully with RealtimeSession');

      // Trigger the initial greeting based on the system prompt
      try {
        if (DEBUG) console.log('🚀 Triggering initial PS survey greeting');
        session.transport.sendEvent({ type: 'response.create' });
      } catch (e) {
        console.warn('Failed to trigger initial response; continuing.', e);
      }
    } catch (error) {
      console.error('❌ Failed to start OpenAI Realtime call:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to connect to OpenAI Realtime',
      );
      setLoadingState(LoadingState.ERROR);
    }
  }, [loadingState, lang, getEphemeralToken, handleUserResponse]);

  // End call handler
  const handleEndCall = useCallback(async () => {
    try {
      if (DEBUG) console.log('📞 Ending OpenAI Realtime call');
      setLoadingState(LoadingState.ENDING);

      // Close RealtimeSession
      if (sessionRef.current) {
        await sessionRef.current.close();
        sessionRef.current = null;
      }

      // Clear agent reference
      if (agentRef.current) {
        agentRef.current = null;
      }

      setCallState(CallState.ENDED);
      setIsSpeaking(false);

      if (DEBUG) console.log('📊 Survey Responses:', surveyResponses);
      toast.success('Post-sales survey completed successfully');

      if (onCallEnded) {
        onCallEnded();
      }
    } catch (error) {
      console.error('❌ Error ending OpenAI Realtime call:', error);
      setCallState(CallState.ENDED);
    } finally {
      setLoadingState(LoadingState.READY);
    }
  }, [surveyResponses, onCallEnded]);

  // Retry initialization
  const retryInitialization = useCallback(() => {
    setError(null);
    setLoadingState(LoadingState.READY);
    setCurrentQuestionIndex(0);
    setSurveyResponses({});
    setTranscript([]);
  }, []);

  // Initialize component and pre-fetch token
  useEffect(() => {
    setLoadingState(LoadingState.READY);

    // Pre-fetch ephemeral token for better performance - non-blocking
    const prefetchToken = async () => {
      const config = version === 3 ? OPENAI_REALTIME_CONFIG_V3 : OPENAI_REALTIME_CONFIG;
      try {
        await fetchEphemeralToken(
          config.model,
          config.voice,
        );
        if (DEBUG) console.log('🚀 Token pre-fetch completed successfully');
      } catch (error) {
        console.warn(
          '⚠️ Failed to pre-fetch token, will fetch when needed:',
          error,
        );
        // Clear any stale token reference on pre-fetch failure
        delete ephemeralTokensRef.current[config.model];
        delete tokenFetchPromisesRef.current[config.model];
      }
    };

    // Run pre-fetch in background without blocking component initialization
    prefetchToken();
  }, [fetchEphemeralToken, version]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (sessionRef.current) {
        sessionRef.current.close().catch(console.error);
      }
    };
  }, []);

  return {
    loadingState,
    error,
    callState,
    sessionId,
    conversationId,
    agentInfo,
    callDuration,
    transcript,
    isSpeaking,
    currentQuestion,
    surveyProgress,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    retryInitialization,
  };
};
